import { useEffect, useState, createContext, useContext, useRef } from "react";

import "../../App.css";
import { useTheme } from "../../context/ThemeContext";
import { useSelectedDesignStore } from "context/SelectedDesignContext";
import { useApi } from "context/ApiProvider";
import { useLoadingComponentStore } from "context/LoadingContext";
import { getAllAdditions, getNewDesign, loadDesign } from "services/designService";
import { useDesignStore } from "context/DesignContext";
import toast, { Toaster } from 'react-hot-toast';
import SideToolbar from "components/Menu/SideToolbar";
import Footer from "components/Footer/Footer";
import LoadingIndicator from "components/LoadingIndicator";
import Viewer3D from "components/Viewer3d/Viewer3D";
import PropertyPanel from "components/PropertyPanel/PropertyPanel";
import TopBar from "components/Menu/TopBar";
import { InsoleDesign } from "models/InsoleDesign";
import { LoadingComponent } from "components/Backdrop/Backdrop";
import { resetStackWithDesign } from "../../components/Viewer3d/UndoStack";
import { useViewerStore, ViewerTabsMode } from "context/ViewerContext";
import cv from "@techstark/opencv-js";
import { LOCAL_STORAGE_KEYS } from "components/Viewer3d/consts";
import MobileDrawer from "components/Drawer/MobileDrawer";
import Chatbox from "components/Chatbox/Chatbox";
import { useAuth } from "context/AuthContext";
import { isExhibitionTourDesign } from "Exhibition/exhibitionCapabilities";
import "Pages/Exhibition/Exhibition.css";
import { canUseDevelopmentUi } from "auth/permissions";

type LoadingState = {
  status: "initializing" | "ready";
  message: string;
};

interface WorkerContextType {
  worker: Worker | null;
}

export const WorkerContext = createContext<WorkerContextType>({ worker: null });

export const useWorker = () => useContext(WorkerContext);

function MainApp() {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>({
    status: "initializing",
    message: "Initializing application",
  });
  const [isShapeCreationFinished, setIsShapeCreationFinished] = useState(true);
  const { isDarkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMesh, setSelectedMesh] = useState<any>(null);
  const designId = useSelectedDesignStore((state) => state.designId);
  const templateReplaced = useSelectedDesignStore((state) => state.templateReplaced);
  const api = useApi();
  const backdropOpen = useLoadingComponentStore((state) => state.backdropOpen);
  const isOpenCVReady = useViewerStore((state) => state.isOpenCVReady);
  const designLoaded = useDesignStore((state) => state.designLoaded);
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [propertyPanelActiveTab, setPropertyPanelActiveTab] = useState<ViewerTabsMode | undefined>(undefined);
  const [propertyPanelRequestId, setPropertyPanelRequestId] = useState(0);
  const { user, isExhibition, exhibitionSession } = useAuth();
  const canUseChatbox = canUseDevelopmentUi(user, isExhibition);

  useEffect(() => {
    
    getAllAdditions(api).then ((allAdds) =>{
      useSelectedDesignStore.setState({additionListNamesFromServer: allAdds});
    });

    const timer = setTimeout(() => {
      if(cv && cv.Mat) {
        useViewerStore.getState().setOpenCVReady(true);
        // console.log("OpenCV.js is ready!");
      } else if (!isOpenCVReady) {
        console.log("Hooking to OpenCV.onRuntimeInitialized");
        cv.onRuntimeInitialized = () => {
          console.log("onRuntimeInitialized invoked, OpenCV.js is ready!");
          useViewerStore.getState().setOpenCVReady(true);
        };
      } else {
        console.log("openCV.js is unloaded or corrupted! need to reload the page!");
        useViewerStore.getState().setOpenCVReady(false);
        window.location.reload();
      }

      setLoadingState({
        status: "ready",
        message: "Application ready",
      });

      const event = new CustomEvent("shapeUpdate", {
        detail: {
          shapeType: "extruded",
          params: {
            height: 2,
            steps: 1,
            depth: 0.5,
            bevelEnabled: true,
            bevelThickness: 0.2,
            bevelSize: 0.1,
            bevelSegments: 3,
          },
        },
      });
      document.dispatchEvent(event);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchAndSetDesign = async () => {
      if (designId < 1) return;
      if (templateReplaced) {
        return;
      }
        useLoadingComponentStore.getState().setVisibile(true);
        await loadDesign(api, designId)
          .then(([design, patient]) => {
            if (design == null) {
              toast.error("Cannot load this design. Please select another one using the “Database” button.");
              throw new Error("design corrupted");
            }
            if(design.designType === 'Template') {
              design.name = useDesignStore.getState().previousDesign?.name ?? "";
              useDesignStore.setState({ temporaryDesign: InsoleDesign.cloneFrom(design)});
            } else {
              useSelectedDesignStore.getState().setCreatedBy(design.createdBy);
            }
            useDesignStore.getState().setDesign(design);
            useDesignStore.setState({ designLoaded: true });
            useDesignStore.setState({ patient: patient });
            useLoadingComponentStore.getState().setVisibile(false);
            resetStackWithDesign(design);
            clearDesignRelatedLocalStorage();
          }).catch((error) => {
              console.error("Error loading design:", error);
              useLoadingComponentStore.getState().setVisibile(false);
          });
    };
    fetchAndSetDesign();
  }, [designId]);

  // The demonstration design opens stripped back: no pressure colour, no bones,
  // no shoe and no additions. The narration reveals each of them as it explains
  // what they are, so they have to be off before it starts rather than switched
  // off by its first step — by then the visitor has already seen them.
  //
  // Applied on load rather than saved with the design because visibility lives
  // in the viewer store, not in the design record.
  const demoVisibilityAppliedRef = useRef(false);
  useEffect(() => {
    if (!designLoaded) {
      demoVisibilityAppliedRef.current = false;
      return;
    }
    if (!isExhibitionTourDesign(designId, exhibitionSession?.demoDesignId)) return;
    if (demoVisibilityAppliedRef.current) return;
    demoVisibilityAppliedRef.current = true;

    const applyHidden = () => {
      useViewerStore.setState({
        coloring: true, // insole colour; the pressure map is the other state
        bonesMeshVisible: false,
        shoeDataMeshVisible: false,
        additionsViewMeshVisible: false,
        liftedInsoleMeshVisible: false,
        insolesVisible: true,
      });
    };

    // Applied several times over the first couple of seconds, not once. The
    // viewer restores the design's own visibility as it finishes building the
    // meshes, and a single call before that landed was simply overwritten —
    // which is why the bones and the shoe were still appearing on load.
    applyHidden();
    const timers = [120, 400, 900, 1600, 2500].map((delay) =>
      window.setTimeout(applyHidden, delay)
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [designLoaded, designId, exhibitionSession?.demoDesignId]);


  const clearDesignRelatedLocalStorage = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.SAVE_AS_NEW_FOR_ADMIN);
  }

  const handleMobileTopBarMenuClick = () => {
    setDrawerOpen(true);
  }

  const handleDrawerCloseClick = () => {
    setDrawerOpen(false);
  }

  const onPropertyPanelTabClick = (tab: ViewerTabsMode | undefined) => {
    if (tab === undefined) {
      setPropertyPanelActiveTab(undefined);
      return;
    }

    setPropertyPanelActiveTab(tab);
    setPropertyPanelRequestId((prev) => prev + 1);
  }

  useEffect(() => {
    const onOpenPanel = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      if (detail.close) {
        onPropertyPanelTabClick(undefined);
        return;
      }
      const tab = detail.tab as ViewerTabsMode | undefined;
      onPropertyPanelTabClick(tab);
    };
    document.addEventListener("voice:openPanel", onOpenPanel as EventListener);
    return () => document.removeEventListener("voice:openPanel", onOpenPanel as EventListener);
  }, []);

  return (
    <div className={`App ${isDarkMode ? "dark-mode" : "light-mode"}`}>
      <TopBar toolbarRef={toolbarRef} onMobileTopBarMenuClick={handleMobileTopBarMenuClick} onPropertyPanelTabClick={onPropertyPanelTabClick}/>
      <MobileDrawer isOpen={isDrawerOpen} onClose={handleDrawerCloseClick}/>
      <SideToolbar />
      <div>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              width: '400px',
              padding: '16px',
              fontSize: '14px',
              borderRadius: '8px',
            },
            success: {
              style: {
                background: '#e6f4ea',
                color: '#276749',
                border: '1px solid #c6f6d5',
              },
              iconTheme: {
                primary: '#38a169',
                secondary: 'white',
              },
            },
            error: {
              style: {
                background: '#ffe6e6',
                color: '#c53030',
                border: '1px solid #feb2b2',
              },
              iconTheme: {
                primary: '#e53e3e',
                secondary: 'white',
              },
            },
          }}
          reverseOrder={true}
        />
      </div>
      <div className="main-content">
        {isLoading && <LoadingIndicator isLoading={isLoading} />}
        <div className="flex-layout">
          <div className="viewer-panel expanded">
            {!isShapeCreationFinished ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p className="loading-message">{loadingState.message}</p>
              </div>
            ) : (
              <div className="three-viewer-container">
                <Viewer3D toolbarRef={toolbarRef} isBackdropOpen={backdropOpen} />
              </div>
            )}
          </div>
          <PropertyPanel
              activeTab={propertyPanelActiveTab}
              openRequestId={propertyPanelRequestId}
            />
        </div>
      </div>
      {canUseChatbox && <Chatbox />}
      <Footer />
      <LoadingComponent open={backdropOpen}></LoadingComponent>
    </div>
  );
}

export default MainApp;
